import { Button } from '@kcftech/design-system';
import { AnalyticsLogResponse } from '@kcftech/prometheustypescriptapiclient';
import { PanelBar, PanelBarItem, PanelBarProps } from '@progress/kendo-react-layout';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { InfoPopup } from 'components/popup/InfoPopup';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { usePollingAnalyticsModelLogs } from 'custom_hooks/data_hooks/analytics_models/useAnalyticsModelLogs';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { AnalyticsLogContent } from 'pages/Models/Development/AnalyticsLogContent';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { tryParseJson } from 'utils/json';

type TimeRange = {
    value: number;
    text: string;
};

const timeRanges: TimeRange[] = [
    {
        value: 30 * 1000,
        text: '30 seconds',
    },
    {
        value: 60 * 1000,
        text: '1 minute',
    },
    {
        value: 5 * 60 * 1000,
        text: '5 minutes',
    },
    {
        value: 10 * 60 * 1000,
        text: '10 minutes',
    },
];

// Styled Components gets real concerned about the type of the raw Kendo
// PanelBar. In theory this wrapper does nothing, but to be safe...
function PanelBarInternal({ children, ...props }: PanelBarProps) {
    return <PanelBar {...props}>{children}</PanelBar>;
}

const StyledPanelBar = styled(PanelBarInternal)`
    .k-item {
        .k-animation-container {
            color: inherit;

            .k-content {
                color: inherit;
                background-color: inherit;
            }
        }
        .k-link.k-state-selected {
            color: inherit;
        }
    }
`;

const logTitle = (log: AnalyticsLogResponse, formatDateTime: (date: Date) => string) => (
    <div className="ellipsis-1-line">
        {formatDateTime(log.Date)} {log.Message}
    </div>
);

type AnalyticsModelLogsProps = {
    modelId: string;
    disableDate?: Date;
};

const renderLabel: DropdownLabelRenderer<TimeRange> = ({ value }) =>
    `Time range: ${value ? value.text : timeRanges[0].text}`;

const renderItem: DropdownItemRenderer<TimeRange> = ({ item }) => item.text;

const getKey = (item: TimeRange) => item.value;

export function AnalyticsModelLogs({ modelId, disableDate }: AnalyticsModelLogsProps) {
    const { formatDateTime } = useUserDateTimeFormat();
    const [polling, setPolling] = useState(false);
    const [selectedTimeRange, setSelectedTimeRange] = useState(timeRanges[0]);
    const { isLoading: logsLoading, logs, isFetching } = usePollingAnalyticsModelLogs({
        modelId,
        pollingEnabled: polling,
        initialTimeRange: selectedTimeRange.value,
        disableEndDate: disableDate,
    });
    const isDisabledTooLong = useMemo(() => {
        if (disableDate) {
            return (
                Math.floor(
                    (new Date().getTime() - disableDate.getTime()) / (1000 * 60 * 60 * 24)
                ) > 90
            );
        }
        return false;
    }, [disableDate]);

    return (
        <SpinnerArea isLoading={logsLoading || isFetching}>
            <div className="px-20">
                <div className="flex-row justify-between pb-20">
                    <div className="flex-row justify-start align-center full-height">
                        <Button
                            type="button"
                            appearance="secondary"
                            onClick={() => setPolling(!polling)}
                            title="Poll logs every 30 seconds"
                            ariaLabel="Poll logs every 30 seconds"
                        >
                            {!polling ? 'Start polling' : 'Stop polling'}
                        </Button>
                    </div>
                    <div className="flex-row justify-flex-end align-center full-height">
                        <Dropdown<TimeRange>
                            data={timeRanges}
                            dataKey={getKey}
                            renderLabel={renderLabel}
                            renderItem={renderItem}
                            value={selectedTimeRange}
                            onChange={setSelectedTimeRange}
                            popup={{ width: '20rem' }}
                        />
                        <InfoPopup title="Logs">
                            <div>
                                Logs only display the last 10 mins, and are limited to 1mb
                                per page request.
                            </div>
                        </InfoPopup>
                    </div>
                </div>
                {!disableDate && (
                    <>
                        {logs.length > 0 ? (
                            <StyledPanelBar className="m-10">
                                {logs.map((log) => {
                                    const messageJson = tryParseJson(log.Message);
                                    return (
                                        <PanelBarItem
                                            key={messageJson['@t'] ?? log.Date.getTime()}
                                            title={logTitle(log, formatDateTime)}
                                        >
                                            <AnalyticsLogContent log={log} />
                                        </PanelBarItem>
                                    );
                                })}
                            </StyledPanelBar>
                        ) : (
                            <div>No logs found.</div>
                        )}
                    </>
                )}
                {disableDate && (
                    <>
                        {isDisabledTooLong ? (
                            <div>
                                This model has been disabled for over 90 days. Logs are
                                only saved for 90 days.
                            </div>
                        ) : (
                            <>
                                {logs.length > 0 ? (
                                    <StyledPanelBar className="m-10">
                                        {logs.map((log) => {
                                            const messageJson = tryParseJson(log.Message);
                                            return (
                                                <PanelBarItem
                                                    key={
                                                        messageJson['@t'] ??
                                                        log.Date.getTime()
                                                    }
                                                    title={logTitle(log, formatDateTime)}
                                                >
                                                    <AnalyticsLogContent log={log} />
                                                </PanelBarItem>
                                            );
                                        })}
                                    </StyledPanelBar>
                                ) : (
                                    <div>No logs found.</div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </SpinnerArea>
    );
}
